import { useTranslation } from "react-i18next"

import type { FilthSources } from "@/api/filth-sources-api"

import { Checkbox } from "@/components/ui/checkbox"

import { toggleFilthSource } from "./library-filth-sources-utils"
import { useAllFilthSources } from "../../hooks/use-filth-sources"
import { sourceTargetDisplay } from "../filth-source-control-utils"

type Props = {
  selected: string[]
  sources?: FilthSources
  loading: boolean
  failed: boolean
  onChange: (uids: string[]) => void
}

export function LibraryFilthSourcesPicker({ selected, sources, loading, failed, onChange }: Props) {
  const { t } = useTranslation()
  const prefix = "adminDashboard.libraries.editor.sources"
  const connectors = sources?.connectors ?? []
  const missing = selected.filter(
    (uid) => !connectors.some((source) => source.connector_uid === uid)
  )
  return (
    <fieldset className="space-y-2 rounded-md border p-3">
      <legend className="px-1 text-sm font-medium">{t(`${prefix}.label`, "Sources")}</legend>
      <p className="text-muted-foreground text-xs">
        {t(
          `${prefix}.help`,
          "No selection means all authorized sources. Selected sources omit memory claims from library results. Changing backend clears the selection."
        )}
      </p>
      {loading && <p role="status">{t(`${prefix}.loading`, "Loading sources…")}</p>}
      {failed && (
        <p role="alert">
          {t(`${prefix}.error`, "Could not load sources. Your saved selection is preserved.")}
        </p>
      )}
      {!loading && !failed && sources?.next && (
        <p role="status">
          {t(
            `${prefix}.incomplete`,
            "Only the first sources are listed; more exist on this backend. Your saved selection is preserved."
          )}
        </p>
      )}
      {!loading && !failed && connectors.length === 0 && (
        <p>{t(`${prefix}.empty`, "No sources registered on this backend.")}</p>
      )}
      {connectors.map((source) => {
        const target = sourceTargetDisplay(source.target || source.connector_uid)
        return (
          <label
            key={source.connector_uid}
            className="flex items-start gap-2 text-sm"
            title={target.raw}
          >
            <Checkbox
              aria-label={target.primary}
              checked={selected.includes(source.connector_uid)}
              className="border-foreground shadow-none"
              disabled={
                loading ||
                failed ||
                (!selected.includes(source.connector_uid) && selected.length >= 64)
              }
              onCheckedChange={(checked) =>
                onChange(toggleFilthSource(selected, source.connector_uid, checked === true))
              }
            />
            <span className="min-w-0 break-words">
              {target.primary}
              <span className="text-muted-foreground block text-xs">
                {target.secondary ? `${target.secondary} · ` : ""}
                {source.kind} ·{" "}
                {source.enabled
                  ? t(`${prefix}.enabled`, "Enabled")
                  : t(`${prefix}.disabled`, "Disabled")}
              </span>
            </span>
          </label>
        )
      })}
      {missing.map((uid) => (
        <label key={uid} className="flex items-start gap-2 text-sm">
          <Checkbox
            checked
            aria-label={uid}
            className="border-foreground shadow-none"
            onCheckedChange={() => onChange(toggleFilthSource(selected, uid, false))}
          />
          <span className="min-w-0 break-words">
            {uid} · {t(`${prefix}.unavailable`, "Unavailable in the current source list")}
          </span>
        </label>
      ))}
    </fieldset>
  )
}

export function LibraryFilthSources({
  backendId,
  selected,
  onChange,
}: {
  backendId: string
  selected: string[]
  onChange: (uids: string[]) => void
}) {
  const sources = useAllFilthSources(backendId, !!backendId)
  return (
    <LibraryFilthSourcesPicker
      failed={sources.isError}
      loading={sources.isPending}
      selected={selected}
      sources={sources.data}
      onChange={onChange}
    />
  )
}
